import React, { useEffect, useMemo, useState } from 'react';
import { Box, useMediaQuery, useTheme } from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { UserInfoWithLzPzus, UserRole } from 'api';
import { ERouteUrl } from 'router/config';
import { Button } from 'components/ui/Button/Buttons';
import { useCheckRole } from 'hooks/useCheckRole';
import {
  AnalyticsIcon,
  ArrowToLeft,
  ArrowToRight,
  HelpIcon,
  IconInfo,
  LogoHuge,
  LogoMiniIcon,
  LzClientIcon,
  LzIcon,
  News,
  OperationIcon,
  PriceIcon,
  Pzus,
  RiskManegment,
  SettingsIcon,
  TransactionsIcon,
  Users,
} from 'assets/svg';
import PzuStore from 'stores/PzuStore';

import { trackEvent } from 'common/amplitude/amplitude';

import { headerMenuBaseUseStyles } from 'components/layout/Header/common/styles/base';

import LoginInfoSection from 'components/layout/Login/LoginContainer/component/LoginInfoSection';

import VersionComponent from 'components/layout/Header/Version/Version';
import { Menu } from '../Header/Header';
import { headerMenuMobileUseStyles } from '../common/styles/mobile';
import { HeaderMenu } from '../HeaderMenu/HeaderMenu';

interface IProp {
  user: UserInfoWithLzPzus;
  setCompactMenu?: (value: boolean) => void;
  compactMenu?: boolean;
}

export const HeaderInformation: React.FC<IProp> = observer(({ user, setCompactMenu, compactMenu }) => {
  const theme = useTheme();
  const matchesTablet = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const classesBase = headerMenuBaseUseStyles({ matchesTablet: Boolean(matchesTablet) });
  const classesMobile = headerMenuMobileUseStyles({ matchesTablet: Boolean(matchesTablet) });
  const classes = { ...classesBase, ...classesMobile };
  const history = useHistory();
  const { pathname } = useLocation();
  const matchesMobile = useMediaQuery(theme.breakpoints.down('xs'), { noSsr: true });
  const [url, setUrl] = useState('');
  // const matchesTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const { isAppAdminRole } = useCheckRole();

  useEffect(() => {
    setUrl(pathname);
  }, [pathname]);

  const handleMenuClick = () => {
    const eventName = compactMenu ? 'Menu_Expand_Click' : 'Menu_RollUp_Click';
    const userData = {
      UserId: Number(user.id),
      UserRole: user.roles[0],
    };
    trackEvent(eventName, userData);
    setCompactMenu && setCompactMenu(!compactMenu);
  };

  const handleOperationsTab = (route: string) => {
    const urlMapping = {
      [ERouteUrl.OPERATIONS_SBER_ID]: ERouteUrl.OPERATIONS_SBER_ID,
      [ERouteUrl.OPERATIONS_PAYMENT_LIST]: ERouteUrl.OPERATIONS_PAYMENT_LIST,
      [ERouteUrl.OPERATIONS_GROUP_COMMISSIONS]: ERouteUrl.OPERATIONS_GROUP_COMMISSIONS,
    } as Record<ERouteUrl, ERouteUrl>;

    const tabPath: ERouteUrl = urlMapping[route as ERouteUrl] || ERouteUrl.OPERATIONS_PSA_LIST;

    return tabPath;
  };

  const lzSubMenu: Menu[] = [
    {
      value: 'info',
      label: 'Общая информация',
      to: 'info',
      visible: true,
    },
    {
      value: 'pzu',
      label: 'Площадки',
      to: 'pzu',
      visible: [UserRole.AppViewer, UserRole.AppAdmin].includes(user.roles[0]),
    },
    {
      value: 'prices',
      label: 'Цены',
      to: 'prices',
      visible: ![UserRole.AppViewer].includes(user.roles[0]),
    },
    {
      value: 'metals',
      label: 'Номенклатура',
      to: 'metals',
      visible: ![UserRole.AppViewer].includes(user.roles[0]),
    },
    {
      value: 'business-card',
      label: 'Бизнес-карты',
      to: 'business-card',
      visible: [UserRole.LzAdmin, UserRole.LzAccountant, UserRole.AppAdmin].includes(user.roles[0]),
    },
    {
      value: 'analytics',
      label: 'Аналитика',
      to: 'analytics',
      visible: [UserRole.AppViewer, UserRole.LzAdmin].includes(user.roles[0]),
    },
  ];

  const lsSubMenu: Menu[] = [
    {
      value: 'list',
      label: 'Общие данные по ломосдатчикам',
      to: '/ls/list',
      visible: true,
    },
    {
      value: 'transaction',
      label: 'История транзакций ломосдатчика',
      to: '/ls/transaction',
      visible: true,
    },
  ];

  const riskSubMenu: Menu[] = [
    {
      value: 'counteragents',
      label: 'Контрагенты',
      to: 'counteragents',
      visible: true,
    },
  ];

  const menu: Menu[] = useMemo(
    () => [
      {
        value: 'psa',
        label: 'Операции',
        to: url && handleOperationsTab(url),
        visible: [
          UserRole.AppAdmin,
          UserRole.LzAdmin,
          UserRole.PzuCashier,
          UserRole.PzuInspector,
          UserRole.LzAccountant,
          UserRole.TechSupport,
          UserRole.AppViewer,
        ].includes(user.roles[0]),
        icon: OperationIcon,
      },
      {
        value: 'analytics',
        label: 'Аналитика',
        to: `/lz/${user.lzId}/analytics`,
        visible: [UserRole.LzAdmin].includes(user.roles[0]),
        icon: AnalyticsIcon,
      },
      {
        value: 'user-list',
        label: isAppAdminRole ? 'Пользователи' : 'Сотрудники',
        to: ERouteUrl.USER_LIST,
        visible: [UserRole.AppAdmin, UserRole.LzAdmin, UserRole.LzAccountant, UserRole.TechSupport].includes(
          user.roles[0],
        ),
        icon: Users,
      },
      {
        value: 'lz',
        label: 'Ломозаготовители',
        to: ERouteUrl.LZ_LIST,
        visible: [UserRole.AppAdmin, UserRole.AppViewer, UserRole.TechSupport].includes(user.roles[0]),
        subMenu: lzSubMenu,
        icon: LzIcon,
      },
      // {
      //   value: 'lz',
      //   label: 'Ломозаготовитель',
      //   to: `/lz/${user.lzId}/info`,
      //   visible: [UserRole.LzAdmin, UserRole.LzAccountant].includes(user.roles[0]),
      //   subMenu: lzSubMenu,
      //   icon: AddFullIcon,
      // },
      {
        value: 'pzu',
        label: 'Площадки',
        to: ERouteUrl.PZU_LIST,
        visible: [UserRole.AppAdmin, UserRole.LzAdmin, UserRole.LzAccountant, UserRole.TechSupport].includes(
          user.roles[0],
        ),
        // subMenu: pzuSubMenu,
        icon: Pzus,
      },
      {
        value: 'prices',
        label: 'Цены',
        to: `/lz/${user.lzId}/prices`,
        visible: [UserRole.LzAdmin, UserRole.LzAccountant].includes(user.roles[0]),
        icon: PriceIcon,
      },
      {
        value: 'info',
        label: 'Информация',
        to: `/lz/${user.lzId}/info`,
        visible: [UserRole.LzAdmin, UserRole.LzAccountant].includes(user.roles[0]) && !matchesMobile,
        icon: IconInfo,
      },

      // {
      //   value: 'payment',
      //   label: 'Платежи',
      //   to: ERouteUrl.PAYMENT_LIST,
      //   visible: [UserRole.AppAdmin, UserRole.LzAdmin, UserRole.PzuCashier, UserRole.LzAccountant].includes(
      //     user.roles[0],
      //   ),
      //   icon: AddFullIcon,
      // },
      {
        value: 'transaction',
        label: 'Транзакции',
        to: ERouteUrl.TRANSACTION_LIST,
        visible: [UserRole.AppViewer].includes(user.roles[0]),
        icon: TransactionsIcon,
      },
      {
        value: 'ls',
        label: 'Ломосдатчики',
        to: ERouteUrl.LS_LIST,
        visible: [UserRole.AppViewer].includes(user.roles[0]),
        subMenu: lsSubMenu,
        icon: LzClientIcon,
      },
      {
        value: 'indexes',
        label: 'Настройка индексов',
        to: ERouteUrl.INDEXES,
        visible: [UserRole.AppViewer].includes(user.roles[0]),
        icon: SettingsIcon,
      },
      {
        value: 'risk-manegment',
        label: 'Риск-менеджмент',
        to: `/risk-manegment/${user.lzId}/counteragents`,
        visible: [UserRole.LzAdmin].includes(user.roles[0]) && !matchesMobile && !matchesTablet,
        subMenu: riskSubMenu,
        icon: RiskManegment,
      },
      {
        value: 'settings',
        label: 'Настройки',
        to: ERouteUrl.SETTINGS,
        visible: [UserRole.AppAdmin].includes(user.roles[0]),
        icon: SettingsIcon,
      },
    ],
    [user, PzuStore.isPriceMenuVisible, url],
  );

  const submenu: Menu[] = useMemo(
    () => [
      {
        value: 'help',
        label: 'Помощь',
        to: ERouteUrl.HELP,
        visible: [
          UserRole.AppAdmin,
          UserRole.LzAdmin,
          UserRole.PzuCashier,
          UserRole.PzuInspector,
          UserRole.LzAccountant,
          UserRole.TechSupport,
        ].includes(user.roles[0]),
        icon: HelpIcon,
      },
      {
        value: 'news',
        label: 'Новости',
        to: ERouteUrl.NEWS,
        visible: [
          UserRole.AppAdmin,
          UserRole.LzAdmin,
          UserRole.PzuCashier,
          UserRole.PzuInspector,
          UserRole.LzAccountant,
        ].includes(user.roles[0]),
        icon: News,
      },
    ],
    [user, PzuStore.isPriceMenuVisible],
  );

  let logoIcon: React.ReactElement | null = <LogoHuge />;
  // let logoIcon: React.ReactElement | null = <img src={NYLogo} alt='logo' />;
  if (compactMenu) {
    logoIcon = <LogoMiniIcon />;
    // logoIcon = <img src={NYLogoCompact} alt='logo' style={{ maxWidth: 65 }} />;
  }

  if (matchesMobile) {
    logoIcon = null;
  }

  return (
    <>
      <div className={classes.header}>
        <div style={{ width: '100%' }}>
          {!matchesTablet && (
            <div
              style={{ marginBottom: 16, textAlign: compactMenu ? 'center' : 'left', cursor: 'pointer' }}
              onClick={() => history.push(ERouteUrl.OPERATIONS_PSA_LIST)}
            >
              {logoIcon}
            </div>
          )}
          <HeaderMenu menu={menu} subMenu={submenu} type='main' compact={compactMenu} user={user} />
        </div>
        {!compactMenu ? (
          <Box className={classes.info}>
            <LoginInfoSection />
            <VersionComponent />
          </Box>
        ) : null}
        {!matchesTablet && (
          <Button
            variant='text'
            onClick={handleMenuClick}
            startIcon={compactMenu ? <ArrowToRight style={{ marginLeft: 10 }} /> : <ArrowToLeft />}
            style={{ justifyContent: compactMenu ? 'center' : 'left', marginTop: '32px' }}
          >
            {compactMenu ? '' : 'Свернуть'}
          </Button>
        )}
      </div>
    </>
  );
});
